import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Skeleton, Stack, LinearProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { newServer } from '../../constants/constants';
import { Link, User } from 'lucide-react';
import DynamicFilters from './DinamicFilters';
import InfiniteScroll from 'react-infinite-scroll-component';
// import HeaderLanding from "components/header-landing/HeaderLanding";

interface Lenguaje {
  lenguaje_name: string;
}

interface Category {
  category_name: string;
}

interface TimeSlot {
  id: string;
  day: string;
  start_time: string;
  end_time: string;
}

interface Lawyer {
  uuid: string;
  first_name: string;
  last_name: string;
  categories: Category[];
  lenguajes: Lenguaje[];
  default_amount: string;
  avatar?: string;
  rating?: number;
  reviews?: number;
  link_appoinment?: string;
  firm_name?: string;
  time_slots?: TimeSlot[];
  logo?: string;
  profilePhoto?: string;
}

interface Firm {
  submerchant_id: number;
  id_submerchant: number;
  name: string;
  status: string;
  appointment_aut: boolean;
  is_firm: boolean;
  lawyers: Lawyer[];
  logo?: string;
}

interface Skill {
  id: number;
  nombre: string;
}

interface Language {
  id: number;
  nombre: string;
}

interface ApiResponse {
  skills: Skill[];
  lenguages: Language[];
  filter: [
    {
      setting_enable_rating: boolean;
    }
  ];
}

interface FilterState {
  practiceArea: string;
  language: string;
  experience: string;
  priceRange: string;
  rating: string;
}

const LawyerDirectory: React.FC = () => {
  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [viewType, setViewType] = useState<'individual' | 'firm'>('individual');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [viewRating, setViewRating] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    practiceArea: '',
    language: '',
    experience: '',
    priceRange: '',
    rating: '',
  });
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [urlUpdated, setUrlUpdated] = useState(false); // Nuevo estado para controlar actualizaciones de URL
  const navigate = useNavigate();
  const location = useLocation();

  // Constante para estilos de fuente
  const FONT_STYLE = { fontFamily: 'Poppins, sans-serif' };

  // Carga de metadatos (skills, lenguajes y configuración de rating)
  useEffect(() => {
    const fetchMetadata = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${newServer}/directory`);
        const responseFilters = await fetch(`${newServer}/filters`);
        const data: ApiResponse = await response.json();
        
        
        setSkills(data.skills);
        setLanguages(data.lenguages);
        setViewRating(data.filter[0]?.setting_enable_rating || false);
      } catch (err) {
        console.error('Error fetching metadata:', err);
      }
    };

    fetchMetadata();
  }, []);

  // Parse URL query parameters and set filters
  useEffect(() => {
    if (skills.length === 0) return; // No procesar hasta que tengamos skills

    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    const searchTerm = params.get('search');

    // Set view type based on URL parameter
    if (type === 'lawyer' || type === 'individual') {
      setViewType('individual');
    } else if (type === 'firm') {
      setViewType('firm');
    }

    // Initialize new filters object
    const newFilters: FilterState = {
      practiceArea: '',
      language: '',
      experience: '',
      priceRange: '',
      rating: '',
    };

    // Process search term if present
    if (searchTerm) {
      // Check if search term matches a practice area
      const matchedSkill = skills.find(
        (skill) => skill.nombre.toLowerCase() === searchTerm.toLowerCase()
      );

      if (matchedSkill) {
        newFilters.practiceArea = matchedSkill.nombre.toLowerCase();
      } else {
        // If not a direct match, try to find a partial match
        const partialMatch = skills.find(
          (skill) =>
            searchTerm.toLowerCase().includes(skill.nombre.toLowerCase()) ||
            skill.nombre.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (partialMatch) {
          newFilters.practiceArea = partialMatch.nombre.toLowerCase();
        }
      }
    }

    // Check for other filter parameters directly in URL
    const practiceArea = params.get('practiceArea');
    const language = params.get('language');
    const experience = params.get('experience');
    const priceRange = params.get('priceRange');
    const rating = params.get('rating');

    if (practiceArea) newFilters.practiceArea = practiceArea;
    if (language) newFilters.language = language;
    if (experience) newFilters.experience = experience;
    if (priceRange) newFilters.priceRange = priceRange;
    if (rating) newFilters.rating = rating;

    // Update active filters with the values from URL sin disparar fetchData inmediatamente
    setActiveFilters(newFilters);
    setUrlUpdated(true); // Indicar que acabamos de actualizar desde la URL
  }, [location.search, skills]);

  // Función que construye la URL con los filtros activos
  const buildFilterUrl = (baseUrl: string, filters: FilterState) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        let paramValue = value;
        if (key === 'priceRange') {
          // Extraer solo los dígitos y el guión
          paramValue = value.replace(/[^\d-]/g, '');
        }
        params.append(key, paramValue);
      }
    });
    return `${baseUrl}&${params.toString()}`;
  };

  // Función para cargar los datos según el tipo de vista y filtros
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl =
        viewType === 'firm' ? `${newServer}/directory/firms?page=1&limit=5` : `${newServer}/directory/individuals?page=1&limit=5`;

      const url = buildFilterUrl(baseUrl, activeFilters);
      console.log('Fetching data from:', url);

      const response = await fetch(url);
      const data = await response.json();
      console.log('Fetched data:', data);

      if (viewType === 'firm') {
        setFirms(data.firms || []);
      } else {
        setLawyers(data.users || []);
      }

      setLoading(false); // Siempre establecer loading a false después de obtener los datos
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false); // También establecer loading a false en caso de error
    }
  }, [activeFilters, viewType]);

  const fetchMoreData = async () => {
    try {
      const nextPage = page + 1;
      const baseUrl =
        viewType === 'firm' ? `${newServer}/directory/firms` : `${newServer}/directory/individuals`;
      const url = buildFilterUrl(`${baseUrl}?page=${nextPage}&limit=5`, activeFilters);
      
      const response = await fetch(url);
      const data = await response.json();
  
      if (viewType === 'firm') {
        // Check if we've reached the last page
        if (data.firms.length === 0 || nextPage > data.totalPages) {
          setHasMore(false);
          return;
        }
        
        setFirms((prevFirms) => [...prevFirms, ...(data.firms || [])]);
      } else {
        // Check if we've reached the last page
        if (data.users.length === 0 || nextPage > data.totalPages) {
          setHasMore(false);
          return;
        }
        
        setLawyers((prevLawyers) => [...prevLawyers, ...(data.users || [])]);
      }
  
      setPage(nextPage);
    } catch (error) {
      console.error('Error fetching more data:', error);
      setHasMore(false);
    }
  };

  // Cargar datos cuando cambien los filtros o el tipo de vista
  useEffect(() => {
    if (skills.length > 0 && urlUpdated) {
      console.log('Fetching data due to filter/viewType change');
      fetchData();
      setUrlUpdated(false); // Resetear para evitar ciclos
    }
  }, [fetchData, skills.length, urlUpdated]);

  // Update browser URL when filters change
  useEffect(() => {
    // Solo actualizar si no estamos procesando una actualización de URL y tenemos metadatos cargados
    if (skills.length > 0 && !urlUpdated) {
      const params = new URLSearchParams();

      // Add type parameter
      params.append('type', viewType === 'firm' ? 'firm' : 'lawyer');

      // Add all active filters
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      // Update the URL without triggering a page reload
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
      console.log('URL updated to:', newUrl);
    }
  }, [activeFilters, viewType, skills.length, urlUpdated]);

  // Actualiza el estado de un filtro en particular
  const handleFilterChange = (filters: Partial<FilterState>) => {
    setActiveFilters((prev) => ({
      ...prev,
      ...filters,
    }));
    setPage(1); // Reset page to 1
    setHasMore(true); // Reset hasMore to true
    // Marcar que necesitamos actualizar los datos
    setUrlUpdated(true);
  };

  const clearFilters = () => {
    setActiveFilters({
      practiceArea: '',
      language: '',
      experience: '',
      priceRange: '',
      rating: '',
    });

    // Marcar que necesitamos actualizar los datos
    setUrlUpdated(true);
  };

  const goToProfile = (uuid: string) => {
    navigate(`/lawyers/${uuid}`);
  };

  // Componente para renderizar la tarjeta de abogado
  const LawyerCard: React.FC<{ lawyer: Lawyer }> = ({ lawyer }) => (
    <div
      onMouseEnter={() => setHoveredCard(lawyer.uuid)}
      onMouseLeave={() => setHoveredCard(null)}
      style={FONT_STYLE}
      className='cursor-pointer hover:translate-y-2 w-full max-w-5xl bg-white rounded-lg shadow-sm p-4'
    >
      <div className='flex sm:flex-col flex-row md:flex-col lg:flex-row items-center gap-4'>
        {/* Avatar */}
        <div className='flex-shrink-0 md:justify-center relative'>
          <div className='absolute top-2 right-8 bg-green-500 rounded-full  border-2 border-white'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-5 w-5 text-white flex justify-center items-center'
              viewBox='0 0 20 20'
              fill='currentColor'
            >
              <path
                fillRule='evenodd'
                d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <img
            src={lawyer.logo || '/avatars/profile.jpg'}
            alt='logo'
            className='w-48 h-48 rounded-full object-contain  bg-black'
          />
        </div>

        {/* Contenido Principal */}
        <div className='flex-grow'>
          <div className='flex sm:flex-col md:flex-col justify-between sm:justify-center items-center'>
            <div className='space-y-1 sm:space-y-0'>
            <div className='flex sm:flex-col items-center justify-center gap-2 sm:gap-0'>
  <h3 className='text-3xl sm:text-5xl font-bold text-green-700 capitalize text-center w-full'>
    {lawyer.first_name} {lawyer.last_name}
  </h3>
  {viewRating && (
    <div className='flex items-center justify-center w-full'>
      <span className='text-yellow-400'>
        {'★'.repeat(Math.round(lawyer.rating || 4))}
      </span>
    </div>
  )}
</div>
              <p
                className='text-gray-700 capitalize text-center md:text-left lg:text-left  text-[20px] sm:text-[20px] sm:text-center'
                style={FONT_STYLE}
              >
                {lawyer.firm_name}
              </p>
              <p className='text-gray-400 mb-4 sm:text-center' style={FONT_STYLE}>
                {lawyer.lenguajes.map((l) => l.lenguaje_name).join(', ')}
              </p>
              <div className='mt-4 flex flex-wrap gap-2 max-w-2xl'>
                {hoveredCard === lawyer.uuid ? (
                  lawyer.categories.map((category, index) => (
                    <span
                      key={index}
                      className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded'
                      style={FONT_STYLE}
                    >
                      {category.category_name}
                    </span>
                  ))
                ) : (
                  <>
                    {lawyer.categories.slice(0, 4).map((category, index) => (
                      <span
                        key={index}
                        className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded'
                        style={FONT_STYLE}
                      >
                        {category.category_name}
                      </span>
                    ))}
                    {lawyer.categories.length > 4 && (
                      <span
                        className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded'
                        style={FONT_STYLE}
                      >
                        +{lawyer.categories.length - 4} more
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className='flex flex-col items-end sm:items-center gap-2'>
              {lawyer.default_amount && (
                <span
                  className='block mx-auto text-center text-[12px] font-bold'
                  style={FONT_STYLE}
                >
                  {Number(lawyer.default_amount.replace('$', '') || '0') > 0
                    ? `Starting at ${lawyer.default_amount} per Appointment`
                    : `Free Appointment`}
                </span>
              )}

              <div>
                <div className='flex justify-around items-center p-4 gap-4'>
                  {/* Botón de Profile */}
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      goToProfile(lawyer.uuid);
                      // Lógica adicional para "Profile" si es necesario
                    }}
                    className='
        group 
        relative 
        flex 
        flex-col 
        items-center 
        justify-center 
        p-4 
        rounded-md 
        hover:bg-blue-50 
        transition-colors 
        focus:outline-none 
        focus:ring-2 
        focus:ring-blue-300
        cursor-pointer
        min-w-[110px]
      '
                  >
                    <User className='w-6 h-6 text-gray-500 group-hover:text-blue-600 mb-1' />
                    <span className='text-gray-700 group-hover:text-blue-600 text-sm font-medium'>
                      Profile
                    </span>
                    {/* TODO Profile Btn, must be appearing with a "Verified" Green Checkmark on bottom right corner of image if Submerchatn has completed their Submerchant Firm Profile, or Elegible User profile. */}
                  </button>

                  {/* Botón de BOOK NOW */}
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (lawyer.link_appoinment) {
                        window.open(lawyer.link_appoinment, '_blank');
                      }
                    }}
                    className='
        group 
        relative 
        flex 
        flex-col 
        items-center 
        justify-center 
        p-4 
        rounded-md 
        hover:bg-blue-50 
        transition-colors 
        focus:outline-none 
        focus:ring-2 
        focus:ring-blue-300
        cursor-pointer
      '
                  >
                    <Link className='w-6 h-6 text-gray-500 group-hover:text-blue-600 mb-1' />
                    <span className='text-gray-700 group-hover:text-blue-600 text-sm font-medium'>
                      BOOK NOW
                    </span>
                  </button>
                  {/* TODO Book Now Btn, must be pulsating as Clickable if Submerchant has successfully configured their Calendar Settings for their Firm or their Elegible User. */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Componente para renderizar la tarjeta de firma
  const FirmCard: React.FC<{ firm: Firm }> = ({ firm }) => (
    <div
      style={FONT_STYLE}
      className='hover:translate-y-1 w-full max-w-4xl bg-white rounded-lg shadow-md border border-gray-100 p-6'
    >
      <div className='flex flex-col sm:flex-col md:flex-col lg:flex-row sm:justify-center sm:items-center gap-6 sm:gap-0'>
        {/* Logo de la firma */}
        <div className='flex-shrink-0 md:justify-center'>
          <img
            src={firm.logo || '/avatars/profile.jpg'}
            alt='logo'
            className='w-48 h-48 rounded-full object-contain mr-8 bg-black'
          />
        </div>

        <div className='mt-4 w-full'>
          <h4 className='text-lg font-semibold mb-2 text-gray-700 text-center'>
            Available Lawyers
          </h4>
          <div className='grid grid-cols-2 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4'>
            {firm.lawyers.map((lawyer) => (
              <div
                key={lawyer.uuid}
                onClick={() => goToProfile(lawyer.uuid)}
                className='bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-sm transition cursor-pointer'
              >
                <div className='flex items-center space-x-3 mb-2'>
                  <img
                    src={lawyer.profilePhoto || '/avatars/profile.jpg'}
                    alt={`${lawyer.first_name} ${lawyer.last_name}`}
                    className='w-12 h-12 rounded-full object-cover'
                  />
                  <div className='flex flex-row justify-between w-full'>
                    <div>
                      <h5 className='font-semibold text-gray-800 capitalize'>
                        {lawyer.first_name} {lawyer.last_name}
                      </h5>
                      <div className='flex flex-col'>
                        {lawyer.lenguajes.length > 0 && (
                          <div className='text-gray-600 text-sm'>
                            <strong>Languages:</strong>{' '}
                            {lawyer.lenguajes.map((l) => l.lenguaje_name).join(', ')}
                          </div>
                        )}
                        {lawyer.categories.length > 0 && (
                          <div className='text-gray-600 text-sm'>
                            <strong>Categories:</strong>{' '}
                            {lawyer.categories.map((c) => c.category_name).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className='text-green-600'>{lawyer.link_appoinment ? '🟢' : '🔴'}</span>
                  </div>
                </div>

                <div className='flex flex-col items-center'>
                  {lawyer.link_appoinment && (
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(lawyer.link_appoinment, '_blank');
                      }}
                    >
                      Book Now
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* <Header /> */}
      <DynamicFilters onFilterChange={handleFilterChange} apiUrl={`${newServer}`} />
      <div className='max-w-4xl mx-auto mb-2'>
        {loading ? (
          <div className='sm:p-10 md:p-10 lg:p-0 p-0 sm:max-w-2xl md:max-w-2xl lg:max-w-4xl  mx-auto'>
            {[...Array(5)].map((_, index) => (
              <Stack
                key={index}
                className='sm:max-w-md md:max-w-lg lg:max-w-7xl mx-auto mb-5 flex sm:flex-col md:flex-col lg:flex-row flex-row md:justify-center md:items-center'
                spacing={1}
              >
                <Skeleton variant='circular' width={120} height={120} />
                <Skeleton animation='wave' height={10} width='100%' />
                <Skeleton variant='rectangular' width='100%' height={50} className='mb-4' />
                <Skeleton variant='rectangular' width='100%' height={50} className='mb-4' />
                <Skeleton animation='wave' height={10} width='100%' />
              </Stack>
            ))}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={viewType === 'firm' ? firms.length : lawyers.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<LinearProgress />}
            endMessage={
              <Typography variant='h6' className='text-center text-gray-600'>
                No more results.
              </Typography>
            }
          >
            <div className='space-y-4'>
              {viewType === 'firm' ? (
                firms.length > 0 ? (
                  firms.map((firm) => <FirmCard key={firm.submerchant_id} firm={firm} />)
                ) : (
                  <Typography variant='h6' className='text-center text-gray-600'>
                    No firms found with the selected filters.
                  </Typography>
                )
              ) : lawyers.length > 0 ? (
                lawyers.map((lawyer) => <LawyerCard key={lawyer.uuid} lawyer={lawyer} />)
              ) : (
                <Typography variant='h6' className='text-center text-gray-600'>
                  No lawyers found with the selected filters.
                </Typography>
              )}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default LawyerDirectory;
